import React, { createRef, useRef, useState } from 'react';
import { AutoSizeCard } from './autoSizeCard';
import { ReactComponent as EditIcon } from './../../../assets/icons/edit.svg';
import { ReactComponent as DeleteIcon } from './../../../assets/icons/delete.svg';
import { ReactComponent as SaveIcon } from './../../../assets/icons/save.svg';
import { ReactComponent as BackIcon } from './../../../assets/icons/back.svg';
import { More } from '../contextMenu/more';
import styles from './noteCard.module.css';
import { IconSwapper } from '../icons/iconSwapper';
import { Button } from '../inputs/button';
import { Load } from '../load/load';
import { useEffect } from 'react';
import { Api } from '../../../services/api';
import { Notify } from '../../../services/notification/notify';
import { Modal } from '../modal/modal';
import { css } from '../../../services/css';

/**
 * @param {Object} props
 * @param {string} props.restEndpoint Rest endpoint to save
 * @param {string} [props.noteKey='note'] Note key
 * @param {boolean} [props.editButton=true] Edit Button
 * @param {boolean} [props.deleteButton=true] Delete Button
 * @param {boolean} [props.thin=false] Thinner Borders
 * @param {JSX.Element|string} props.title Title
 * @param {() => void} [props.refreshData] Refresh Data
 * @param {string} props.children Children
 */
export const NoteCard = ({
    restEndpoint,
    noteKey = 'note',
    children,
    editButton = true,
    deleteButton = true,
    thin = false,
    title = <></>,
    refreshData = () => {},
    ...props
}) => {

    const [value, setValue] = useState(children);
    const [isEditing, setIsEditing] = useState(false);
    const [isSavingEdits, setIsSavingEdits] = useState(false);
    const [promptDelete, setPromptDelete] = useState(false);

    const ref = useRef(null);

    useEffect(() => {
        setValue(children.toString());
    }, [children])

    const actions = () => {
        const a = [];

        if (editButton)
            a.push({
                label: 'Edit Note',
                icon: <EditIcon />,
                onClick: () => {setIsEditing(true);}
            });
        
        if (deleteButton)
            a.push({
                label: 'Delete Note',
                icon: <DeleteIcon />,
                onClick: () => {setPromptDelete(true);}
            });

        return a;
    }

    /**
     * @param {string|null} val Value to say. Use null to save value
     */
    const saveNote = (val = null) => {
        setIsSavingEdits(true);
        setIsEditing(false);

        if (val)
            setValue(val);

        Api.patch(restEndpoint, {[noteKey]: val ?? value})
        .then(json => {
            if (!json.success)
                throw new Error('Request was not successful');

            setIsSavingEdits(false);
            setPromptDelete(false);
            refreshData();
        })
        .catch(e => {
            console.error(e);
            Notify.error('Error saving', 'An error occurred while saving the note. Try again.');
            setIsSavingEdits(false);
            setValue(children);
        });
    }

    useEffect(() => {
        resize();
    }, [value]);

    const resize = () => {
        if (ref.current) {
            // @ts-ignore
            ref.current.style.height = 'auto';
            // @ts-ignore
            ref.current.style.height = ref.current.scrollHeight + 'px';
        }
    }

    // Focus at the end of the text area, not the start
    const handleFocus = (e) => {
        const textarea = e.target;
        textarea.setSelectionRange(textarea.value.length, textarea.value.length);
    }

    const handleInput = (e) => {
        setValue(e.target.value);
    }

    return <AutoSizeCard
        className={css(styles.NoteCard, thin && styles.Thin)}
        onClick={(e) => {
            if (isEditing && ref.current && e.target !== ref.current) {
                ref.current.focus();
                ref.current.setSelectionRange(ref.current.value.length, ref.current.value.length);
            }
        }}
        {...props}
    >
         <Modal
            header={'Delete Note'}
            active={promptDelete}
            buttons={[
                {label: 'No', primary: false, closeOnClick: true},
                {label: 'Yes', primary: true, onClick: () => { saveNote(''); }, disabled: isSavingEdits}
            ]}
            onClose={() => { setPromptDelete(false); }}
        >
            Are you sure want to delete the note? This action is irreversible and the note can not be restored.
        </Modal>

        <h1>{title}</h1>
        {isEditing ? (
            <textarea
                maxLength={4000}
                autoFocus={true}
                ref={ref}
                rows={1}
                className={styles.Note}
                disabled={!isEditing}
                onInput={handleInput}
                onFocus={handleFocus}
                value={value}
            ></textarea>
        ) : (
            <div className={styles.Note}>
                {children}
            </div>
        )}
        {(actions().length > 0) ?
            <IconSwapper
            icons={[
                {
                    icon: <More actions={actions()} />,
                    exit: 'top',
                    active: !isEditing && !isSavingEdits
                },
                {
                    icon: <div className={styles.EditActions}>
                        <Button
                            className={styles.Btn + ' ' + styles.SaveBtn}
                            onClick={() => { saveNote(); }}
                            disabled={isSavingEdits}
                        ><div className='icon'><SaveIcon /></div></Button>
                        <Button
                            className={styles.Btn + ' ' + styles.BackBtn}
                            onClick={() => { setIsEditing(false); setValue(children); }}
                            disabled={isSavingEdits}
                        ><div className='icon'><BackIcon /></div></Button>
                    </div>,
                    exit: 'right',
                    active: isEditing
                },
                {
                    icon: <div className={styles.SavingAction}>
                        <div className={styles.SaveLoading}><Load /></div>
                    </div>,
                    exit: 'top',
                    active: isSavingEdits
                }
            ]}
        /> : ''}
    </AutoSizeCard>
}